import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useApp } from "@/context/AppContext";
import { Seo } from "@/components/Seo";
import { getRecentLots, getPrices, getStats } from "@/lib/api";
import { LotCard } from "@/components/LotCard";
import { PriceTable } from "@/components/PriceTable";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Handshake, ShieldCheck, LineChart, Ship,
  FileText, MessagesSquare, CheckCircle2, ArrowRight, Clock,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1618100790047-3da47dcd5727?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxvbGl2ZSUyMG9yY2hhcmQlMjBoYXJ2ZXN0JTIwc3Vuc2V0fGVufDB8fHx8MTc4NDI1MzgwMXww&ixlib=rb-4.1.0&q=85&w=1600";

const VALUES = [
  { icon: Handshake, k: "direct" },
  { icon: ShieldCheck, k: "verified" },
  { icon: LineChart, k: "transparency" },
  { icon: Ship, k: "export" },
];

const STEPS = [
  { icon: FileText, k: "step1", n: "01" },
  { icon: MessagesSquare, k: "step2", n: "02" },
  { icon: CheckCircle2, k: "step3", n: "03" },
];

export default function Home() {
  const { t, formatNumber } = useApp();
  const { data: lots = [] } = useQuery({ queryKey: ["recent-lots"], queryFn: () => getRecentLots(6) });
  const { data: prices = [] } = useQuery({ queryKey: ["prices"], queryFn: getPrices });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: getStats });

  const statItems = [
    { key: "hero.stat.lots", value: stats?.lots },
    { key: "hero.stat.mills", value: stats?.mills },
    { key: "hero.stat.regions", value: stats?.regions },
    { key: "hero.stat.members", value: stats?.members },
  ];

  return (
    <div>
      <Seo title={t("seo.home.title")} description={t("seo.home.desc")} jsonLd={{ "@context": "https://schema.org", "@graph": [{ "@type": "Organization", name: "EvooTunisia", url: "https://evootunisia.com/", logo: "https://evootunisia.com/og-default.jpg", description: t("seo.home.desc") }, { "@type": "WebSite", name: "EvooTunisia", url: "https://evootunisia.com/" }] }} />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a2010]/95 via-[#2C3518]/80 to-[#2C3518]/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <div className="max-w-2xl">
            <Badge className="mb-5 border border-accent/40 bg-accent/15 text-accent hover:bg-accent/15">
              {t("hero.badge")}
            </Badge>
            <h1 className="font-serif text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("hero.title")}
            </h1>
            <p className="mt-6 max-w-xl text-base text-white/80 sm:text-lg">{t("hero.subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/marche/vrac">
                <Button data-testid="hero-cta-primary" size="lg" className="h-12 bg-accent px-7 text-accent-foreground hover:bg-accent/90">
                  {t("hero.cta.primary")} <ArrowRight className="ms-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/inscription">
                <Button data-testid="hero-cta-secondary" size="lg" variant="outline" className="h-12 border-white/40 bg-white/5 px-7 text-white hover:bg-white/15 hover:text-white">
                  {t("hero.cta.secondary")}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* stats bar */}
        <div className="relative border-t border-white/10 bg-black/20 backdrop-blur-sm">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 py-6 sm:px-6 lg:grid-cols-4 lg:px-8">
            {statItems.map((s) => (
              <div key={s.key} data-testid={`stat-${s.key}`} className="text-center lg:text-start">
                <p className="font-serif text-3xl font-bold text-accent">
                  {s.value != null ? formatNumber(s.value) : "—"}
                  {s.key === "hero.stat.members" ? "+" : ""}
                </p>
                <p className="text-sm text-white/70">{t(s.key)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-serif text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">{t("value.heading")}</h2>
          <p className="mt-3 text-neutral-600">{t("value.subtitle")}</p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map(({ icon: Icon, k }) => (
            <div key={k} data-testid={`value-${k}`} className="rounded-lg border border-border bg-white p-6 transition-transform duration-200 hover:-translate-y-1">
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-serif text-xl font-semibold text-neutral-900">{t(`value.${k}.title`)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{t(`value.${k}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RECENT LOTS */}
      <section className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <h2 className="font-serif text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">{t("lots.heading")}</h2>
              <p className="mt-3 text-neutral-600">{t("lots.subtitle")}</p>
            </div>
            <Link to="/marche/vrac">
              <Button variant="outline" data-testid="lots-view-all" className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground">
                {t("lots.viewAll")} <ArrowRight className="ms-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {lots.map((lot) => <LotCard key={lot.id} lot={lot} />)}
          </div>
        </div>
      </section>

      {/* MARKET PRICES */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="font-serif text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">{t("prices.heading")}</h2>
            <p className="mt-3 text-neutral-600">{t("prices.subtitle")}</p>
            <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-neutral-500">
              <Clock className="h-3.5 w-3.5" /> {t("prices.updated")}
            </p>
            <div className="mt-6">
              <Link to="/prix">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="prices-view-all">
                  {t("prices.viewAll")} <ArrowRight className="ms-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-3">
            <PriceTable prices={prices} />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#2C3518] py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl font-bold tracking-tight text-white sm:text-4xl">{t("how.heading")}</h2>
            <p className="mt-3 text-white/70">{t("how.subtitle")}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map(({ icon: Icon, k, n }) => (
              <div key={k} data-testid={`how-${k}`} className="relative rounded-lg border border-white/10 bg-white/5 p-8">
                <span className="absolute end-6 top-6 font-serif text-4xl font-bold text-accent/30">{n}</span>
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-accent text-accent-foreground">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-serif text-xl font-semibold text-white">{t(`how.${k}.title`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{t(`how.${k}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-primary to-[#2C3518] px-8 py-14 text-center sm:px-16">
          <h2 className="mx-auto max-w-2xl font-serif text-3xl font-bold tracking-tight text-white sm:text-4xl">{t("cta.heading")}</h2>
          <p className="mx-auto mt-4 max-w-xl text-white/80">{t("cta.subtitle")}</p>
          <Link to="/inscription">
            <Button data-testid="cta-button" size="lg" className="mt-8 h-12 bg-accent px-8 text-accent-foreground hover:bg-accent/90">
              {t("cta.button")} <ArrowRight className="ms-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
